import "@/App.css";
import "@/index.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/layout/ProtectedRoute";
import AppLayout from "@/components/layout/AppLayout";
import ErrorBoundary from "@/components/layout/ErrorBoundary";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import ClientsList from "@/pages/ClientsList";
import ClientForm from "@/pages/ClientForm";
import ClientDetail from "@/pages/ClientDetail";
import ProjectsList from "@/pages/ProjectsList";
import ProjectNew from "@/pages/ProjectNew";
import ProjectBoard from "@/pages/ProjectBoard";
import Integrations from "@/pages/Integrations";
import Settings from "@/pages/Settings";
import PublicReview from "@/pages/PublicReview";
import Pending from "@/pages/Pending";
import Profile from "@/pages/Profile";
import ChangePassword from "@/pages/ChangePassword";
import CeoOnboarding from "@/pages/CeoOnboarding";
import QuotaModal from "@/components/QuotaModal";
import { trackPageview } from "@/lib/analytics";

// SPA pageview tracker — mounted ONCE inside <BrowserRouter>, fires a
// sanitised GA `page_view` on every route change. Production-only gating
// + token stripping live in src/lib/analytics.js.
function RouteChangeTracker() {
    const { pathname, search } = useLocation();
    useEffect(() => {
        trackPageview(pathname + search);
    }, [pathname, search]);
    return null;
}

export default function App() {
    return (
        <ErrorBoundary>
            <AuthProvider>
                <BrowserRouter>
                    <RouteChangeTracker />
                    <QuotaModal />
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/services" element={<Services />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/privacy" element={<Privacy />} />
                        <Route path="/terms" element={<Terms />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/review/:token" element={<PublicReview />} />
                        <Route path="/share/:projectId/:token" element={<PublicReview />} />

                        {/* Special protected routes that DO NOT use the
                            normal app sidebar (forced password change,
                            pending screen, and the admin control panel
                            take over the viewport). */}
                        <Route
                            path="/change-password"
                            element={<ProtectedRoute><ChangePassword /></ProtectedRoute>}
                        />
                        <Route
                            path="/pending"
                            element={<ProtectedRoute><Pending /></ProtectedRoute>}
                        />
                        <Route
                            path="/profile"
                            element={<ProtectedRoute><Profile /></ProtectedRoute>}
                        />
                        <Route
                            path="/admin"
                            element={<ProtectedRoute><Admin /></ProtectedRoute>}
                        />
                        <Route
                            path="/ceo/onboard"
                            element={<ProtectedRoute><CeoOnboarding /></ProtectedRoute>}
                        />

                        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/clients" element={<ClientsList />} />
                            <Route path="/clients/new" element={<ClientForm />} />
                            <Route path="/clients/:id" element={<ClientDetail />} />
                            <Route path="/clients/:id/edit" element={<ClientForm />} />
                            <Route path="/projects" element={<ProjectsList />} />
                            <Route path="/projects/new" element={<ProjectNew />} />
                            <Route path="/projects/:id" element={<ProjectBoard />} />
                            <Route path="/integrations" element={<Integrations />} />
                            <Route path="/settings" element={<Settings />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </ErrorBoundary>
    );
}
